import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Bee — a round striped bumblebee, storybook style: one plump fuzzy ball of a
// body wearing charcoal stripes, a big friendly face up front, two stubby pale
// wings on its back, bobble-tipped antennae and a blunt, harmless little
// stinger behind. Faces +Z, stands on six tiny legs at y = 0. About 0.3 m tall
// to the antenna tips.
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "body"   : the striped ball + face + antennae. Bobs in idle.
//   - "wing-l" / "wing-r" : the stubby wings. Flutter fast (the signature).
// The six tiny legs and the stinger are static (unnamed) so the feet always
// stay planted on the ground.

const YELLOW = "#f2c14e";     // warm bumblebee gold
const YELLOW_DARK = "#d9a63a";// shaded underside
const STRIPE = "#2c2f36";     // deep charcoal stripes (never pure black)
const WING = "#f6efe3";       // pale cream wings
const EYE_WHITE = "#f7f3e8";  // eye whites (never pure white)
const PUPIL = "#332b33";      // dark plum pupils

// The pivots the animations move the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0, 0];            // ground contact
const WING_L_PIVOT: [number, number, number] = [-0.045, 0.205, -0.03];
const WING_R_PIVOT: [number, number, number] = [0.045, 0.205, -0.03];

// One big friendly eye: white ball, dark pupil slightly forward, tiny highlight.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.028, 10, 8), color: EYE_WHITE,
      position: [x, 0.165, 0.096], name: "body" },
    { geometry: new THREE.SphereGeometry(0.015, 8, 6), color: PUPIL,
      position: [x, 0.165, 0.116], name: "body" },
    { geometry: new THREE.SphereGeometry(0.006, 6, 5), color: WING,
      position: [x + 0.007, 0.173, 0.126], name: "body" },
  ];
}

// One antenna: a thin stalk angled up-forward with a little charcoal bobble.
function antenna(side: 1 | -1): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.006, 0.008, 0.085, 6), color: STRIPE,
      position: [side * 0.038, 0.255, 0.075], rotation: [0.5, 0, -side * 0.35], name: "body" },
    { geometry: new THREE.SphereGeometry(0.016, 8, 6), color: STRIPE,
      position: [side * 0.052, 0.292, 0.096], name: "body" },
  ];
}

// One stubby wing: a flattened cream ellipse growing out of the back, angled
// slightly up so it catches the light. Named so it can flutter about its root.
function wing(side: 1 | -1): Part {
  const name = side > 0 ? "wing-r" : "wing-l";
  return {
    geometry: new THREE.SphereGeometry(0.085, 10, 8), color: WING,
    position: [side * 0.105, 0.225, -0.045],
    rotation: [0.1, side * 0.35, side * 0.35],
    scale: [1.0, 0.16, 0.55], name,
  };
}

// One tiny leg: a dark stub reaching from the belly to the ground.
function leg(side: 1 | -1, z: number): Part {
  return {
    geometry: new THREE.CylinderGeometry(0.011, 0.013, 0.07, 6), color: STRIPE,
    position: [side * 0.058, 0.033, z], rotation: [0, 0, -side * 0.35],
  };
}

export const bee = defineModel({
  id: "bee",
  batchId: "mess",
  name: "Bee",
  tags: ["mess", "cute", "animated", "creature", "insect", "bee", "bug", "garden", "tiny"],
  summary: "A round striped bumblebee with stubby cream wings and bobble antennae.",
  description:
    "A storybook bumblebee: one plump golden ball striped in charcoal, a big " +
    "friendly face with bead-bright eyes, bobble-tipped antennae, two stubby " +
    "pale wings and a blunt little stinger, standing on six tiny legs. Bobs " +
    "softly at rest (idle) and blurs its wings in a fast flutter (flutter).",
  author: "models",
  footprint: 0.2,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── body (named, bobs) ───────────────────────────────────────────────
      // The main golden ball, slightly longer than tall.
      { geometry: new THREE.SphereGeometry(0.10, 12, 10), color: YELLOW,
        position: [0, 0.125, -0.01], scale: [1.0, 0.95, 1.25], name: "body" },
      // A shaded golden underside.
      { geometry: new THREE.SphereGeometry(0.085, 10, 8), color: YELLOW_DARK,
        position: [0, 0.105, -0.02], scale: [1.0, 0.75, 1.25], name: "body" },
      // Two charcoal stripes hugging the abdomen (sized to the local
      // cross-section so they read as paint, not hoops).
      { geometry: new THREE.SphereGeometry(0.096, 12, 10), color: STRIPE,
        position: [0, 0.125, -0.05], scale: [1.0, 0.92, 0.26], name: "body" },
      { geometry: new THREE.SphereGeometry(0.080, 12, 10), color: STRIPE,
        position: [0, 0.122, -0.09], scale: [1.0, 0.90, 0.24], name: "body" },
      // A charcoal tail cap closing the abdomen behind the second stripe.
      { geometry: new THREE.SphereGeometry(0.055, 10, 8), color: STRIPE,
        position: [0, 0.12, -0.115], scale: [1.0, 0.9, 0.9], name: "body" },
      // The round face mass up front.
      { geometry: new THREE.SphereGeometry(0.078, 12, 10), color: YELLOW,
        position: [0, 0.15, 0.055], name: "body" },
      ...eye(0.04), ...eye(-0.04),
      ...antenna(1), ...antenna(-1),

      // ── wings (named, flutter) ───────────────────────────────────────────
      wing(1), wing(-1),

      // ── stinger + legs (static) ──────────────────────────────────────────
      // A blunt, rounded little stinger — friendly, not sharp.
      { geometry: new THREE.ConeGeometry(0.032, 0.06, 8), color: STRIPE,
        position: [0, 0.125, -0.155], rotation: [-Math.PI / 2, 0, 0] },
      leg(1, 0.05), leg(1, 0), leg(1, -0.05),
      leg(-1, 0.05), leg(-1, 0), leg(-1, -0.05),
    ];
  },
  animations: [
    // Idle — a soft, happy bob: the body rises a touch and rocks forward while
    // the wings ride along with a lazy sway. Reads as "humming to itself".
    {
      id: "idle",
      label: "Idle bob",
      duration: 2.4,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.2, value: [0, 0.014, 0], ease: "sine" },
            { t: 2.4, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.03, 0, 0], ease: "sine" },
            { t: 1.2, value: [-0.03, 0, 0], ease: "sine" },
            { t: 2.4, value: [0.03, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "wing-l", pivot: WING_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0.08], ease: "sine" },
            { t: 1.2, value: [0, 0, -0.08], ease: "sine" },
            { t: 2.4, value: [0, 0, 0.08], ease: "sine" },
          ],
        },
        {
          target: "wing-r", pivot: WING_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, -0.08], ease: "sine" },
            { t: 1.2, value: [0, 0, 0.08], ease: "sine" },
            { t: 2.4, value: [0, 0, -0.08], ease: "sine" },
          ],
        },
      ],
    },

    // Flutter — the signature move: both wings beat fast about their roots
    // while the body lifts a whisker, ready to take off.
    {
      id: "flutter",
      label: "Wing flutter",
      duration: 0.4,
      loop: true,
      tracks: [
        {
          target: "wing-l", pivot: WING_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0.55], ease: "sine" },
            { t: 0.1, value: [0, 0, -0.35], ease: "sine" },
            { t: 0.2, value: [0, 0, 0.55], ease: "sine" },
            { t: 0.3, value: [0, 0, -0.35], ease: "sine" },
            { t: 0.4, value: [0, 0, 0.55], ease: "sine" },
          ],
        },
        {
          target: "wing-r", pivot: WING_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, -0.55], ease: "sine" },
            { t: 0.1, value: [0, 0, 0.35], ease: "sine" },
            { t: 0.2, value: [0, 0, -0.55], ease: "sine" },
            { t: 0.3, value: [0, 0, 0.35], ease: "sine" },
            { t: 0.4, value: [0, 0, -0.55], ease: "sine" },
          ],
        },
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.2, value: [0, 0.010, 0], ease: "sine" },
            { t: 0.4, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
